// Story 1.30 — admin Bulletin composer at /admin/bulletin/new (new draft)
// AND /admin/bulletin/:id (edit existing). Wrapped by <AdminGuard>.
//
// Form: Title + Slug (auto-derived from title; admin-editable; locked
// after publish) + Body (Markdown textarea) + Preview Tab. Action bar
// with Save draft / Publish. Linked-dossier picker hidden until Epic 2
// ships the drops table.

import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Card,
  CardContent,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@greenhold/components";

import { slugify } from "../lib/slugify";
import { useAdminBulletinDetail } from "../features/admin/useAdminBulletinDetail";
import { useCreateBulletinDraft } from "../features/admin/useCreateBulletinDraft";
import { useUpdateBulletinPost } from "../features/admin/useUpdateBulletinPost";
import { usePublishBulletinPost } from "../features/admin/usePublishBulletinPost";

const TITLE_MAX = 200;
const SLUG_MAX = 100;
const BODY_MAX = 100_000;

const SLUG_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export function AdminBulletinComposerPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();

  const detailQuery = useAdminBulletinDetail(id);
  const createMut = useCreateBulletinDraft();
  const updateMut = useUpdateBulletinPost();
  const publishMut = usePublishBulletinPost();

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [body, setBody] = useState("");
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const slugInputRef = useRef<HTMLInputElement | null>(null);

  // Load existing post into form when in edit mode.
  useEffect(() => {
    if (isEdit && detailQuery.data) {
      const post = detailQuery.data.data;
      setTitle(post.title);
      setSlug(post.slug);
      setBody(post.body_md);
      // In edit mode, treat the slug as already-set (don't overwrite on title typing).
      setSlugManuallyEdited(true);
    }
  }, [isEdit, detailQuery.data]);

  const isPublished = !!detailQuery.data?.data.published_at;

  function onTitleChange(value: string) {
    setTitle(value);
    if (!slugManuallyEdited && !isPublished) {
      setSlug(slugify(value));
    }
  }

  function onSlugChange(value: string) {
    setSlug(value);
    setSlugManuallyEdited(true);
  }

  const titleValid =
    title.trim().length > 0 && title.length <= TITLE_MAX && title.trim() === title;
  const slugValid =
    slug.length > 0 && slug.length <= SLUG_MAX && SLUG_REGEX.test(slug);
  const bodyValid = body.length > 0 && body.length <= BODY_MAX;
  const allValid = titleValid && slugValid && bodyValid;

  function handleSlugConflict(slug: string | undefined) {
    if (slug) {
      toast.error(`Slug "${slug}" already exists — pick another.`);
      slugInputRef.current?.focus();
    }
  }

  async function onSaveDraft() {
    if (!allValid) return;
    if (isEdit && id) {
      try {
        await updateMut.mutateAsync({
          id,
          title: title !== detailQuery.data?.data.title ? title : undefined,
          slug: slug !== detailQuery.data?.data.slug ? slug : undefined,
          body_md: body !== detailQuery.data?.data.body_md ? body : undefined,
        });
        toast.success("Saved.");
      } catch (err) {
        const e = err as Error & { code?: string; slug?: string };
        if (e.code === "BULLETIN_SLUG_CONFLICT") {
          handleSlugConflict(e.slug);
        } else if (e.code === "BULLETIN_SLUG_IMMUTABLE_AFTER_PUBLISH") {
          toast.error("Slug is locked after publish (URL stability).");
        } else {
          toast.error(e.message);
        }
      }
    } else {
      try {
        const resp = await createMut.mutateAsync({
          title,
          slug,
          body_md: body,
        });
        toast.success("Draft saved.");
        navigate(`/admin/bulletin/${resp.data.id}`);
      } catch (err) {
        const e = err as Error & { code?: string; slug?: string };
        if (e.code === "BULLETIN_SLUG_CONFLICT") {
          handleSlugConflict(e.slug);
        } else {
          toast.error(e.message);
        }
      }
    }
  }

  async function onConfirmPublish() {
    setShowPublishConfirm(false);
    if (!id) {
      // New-draft path: save first, then publish in sequence.
      try {
        const created = await createMut.mutateAsync({
          title,
          slug,
          body_md: body,
        });
        await publishMut.mutateAsync({ id: created.data.id });
        toast.success(
          `Published — public URL will be /bulletin/${created.data.slug} once Story 2.9 ships.`
        );
        navigate(`/admin/bulletin/${created.data.id}`);
      } catch (err) {
        const e = err as Error & { code?: string; slug?: string };
        if (e.code === "BULLETIN_SLUG_CONFLICT") {
          handleSlugConflict(e.slug);
        } else {
          toast.error(e.message);
        }
      }
      return;
    }
    // Edit path: publish the existing row.
    try {
      const resp = await publishMut.mutateAsync({ id });
      toast.success(
        `Published — public URL will be /bulletin/${resp.data.slug} once Story 2.9 ships.`
      );
    } catch (err) {
      const e = err as Error & { code?: string };
      if (e.code === "BULLETIN_ALREADY_PUBLISHED") {
        toast.error("Already published.");
      } else {
        toast.error(e.message);
      }
    }
  }

  const mutationsPending =
    createMut.isPending || updateMut.isPending || publishMut.isPending;

  if (isEdit && detailQuery.isPending) {
    return (
      <main className="min-h-screen bg-brand-paper-200 text-brand-forest-900 p-6">
        <div className="max-w-6xl mx-auto">
          <p className="opacity-70 text-sm">Loading post…</p>
        </div>
      </main>
    );
  }
  if (isEdit && detailQuery.isError) {
    return (
      <main className="min-h-screen bg-brand-paper-200 text-brand-forest-900 p-6">
        <div className="max-w-6xl mx-auto space-y-3">
          <p className="text-destructive">
            {detailQuery.error.message ?? "Failed to load post."}
          </p>
          <Button variant="secondary" onClick={() => navigate("/admin/bulletin")}>
            ← Back to list
          </Button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-brand-paper-200 text-brand-forest-900 p-6 pb-24">
      <div className="max-w-6xl mx-auto space-y-6">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold">
              {isEdit ? "Edit bulletin post" : "New bulletin post"}
            </h1>
            <p className="opacity-70 text-sm">
              {isPublished
                ? "Published. Slug is locked; you can still edit the title + body."
                : "Draft. Not visible to the public until you click Publish."}
            </p>
          </div>
          <Button
            variant="tertiary"
            onClick={() => navigate("/admin/bulletin")}
          >
            ← Back to list
          </Button>
        </header>

        <Tabs defaultValue="write" className="w-full">
          <TabsList>
            <TabsTrigger value="write">Write</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>

          <TabsContent value="write">
            <Card>
              <CardContent className="space-y-4 p-6">
                <label className="block text-sm">
                  <span className="block mb-1 opacity-70">
                    Title <span className="font-mono">({title.length} / {TITLE_MAX})</span>
                  </span>
                  <input
                    type="text"
                    className="w-full border rounded px-3 py-2 bg-white"
                    value={title}
                    maxLength={TITLE_MAX}
                    onChange={(e) => onTitleChange(e.target.value)}
                  />
                </label>

                <label className="block text-sm">
                  <span className="block mb-1 opacity-70">
                    Slug{" "}
                    <span className="font-mono">({slug.length} / {SLUG_MAX})</span>
                    {!slugManuallyEdited && !isEdit ? (
                      <em className="ml-2 opacity-60">auto-derived from title</em>
                    ) : null}
                    {isPublished ? (
                      <em className="ml-2 opacity-60">
                        — locked after publish (URL stability)
                      </em>
                    ) : null}
                  </span>
                  <input
                    ref={slugInputRef}
                    type="text"
                    className="w-full border rounded px-3 py-2 bg-white font-mono text-sm"
                    value={slug}
                    maxLength={SLUG_MAX}
                    disabled={isPublished}
                    onChange={(e) => onSlugChange(e.target.value)}
                  />
                  {!slugValid && slug.length > 0 ? (
                    <p className="text-xs text-destructive mt-1">
                      Slug must be lowercase ASCII + digits + single hyphens
                      (e.g. <code className="font-mono">may-2026-streamer-drop</code>).
                    </p>
                  ) : null}
                </label>

                <label className="block text-sm">
                  <span className="block mb-1 opacity-70">
                    Body (Markdown){" "}
                    <span className="font-mono">({body.length} / {BODY_MAX})</span>
                  </span>
                  <textarea
                    className="w-full border rounded px-3 py-2 bg-white font-mono text-sm"
                    rows={20}
                    value={body}
                    maxLength={BODY_MAX}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="## Coming this month&#10;&#10;Markdown body — supports tables, code blocks, links."
                  />
                </label>

                <div className="text-xs opacity-60 italic">
                  Linking to a drop becomes available when Epic 2's drops
                  table ships.
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="preview">
            <Card>
              <CardContent className="p-6">
                <article className="prose prose-sm max-w-none">
                  <h1>{title || <em className="opacity-60">Untitled</em>}</h1>
                  <p className="opacity-70 text-xs font-mono not-prose">
                    /bulletin/{slug || <em className="opacity-60">slug</em>}
                  </p>
                  {body ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {body}
                    </ReactMarkdown>
                  ) : (
                    <p className="opacity-60 italic">
                      Body preview appears here as you type.
                    </p>
                  )}
                </article>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Sticky action bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-brand-paper-50 border-t border-brand-paper-200 p-4">
        <div className="max-w-6xl mx-auto flex items-center justify-end gap-3">
          <Button
            variant="secondary"
            onClick={onSaveDraft}
            disabled={!allValid || mutationsPending}
          >
            {isEdit ? "Save changes" : "Save draft"}
          </Button>
          {!isPublished ? (
            <Button
              variant="primary"
              onClick={() => setShowPublishConfirm(true)}
              disabled={!allValid || mutationsPending}
            >
              {isEdit ? "Publish" : "Publish now"}
            </Button>
          ) : null}
        </div>
      </div>

      <AlertDialog
        open={showPublishConfirm}
        onOpenChange={setShowPublishConfirm}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publish bulletin post?</AlertDialogTitle>
            <AlertDialogDescription>
              Publishing makes this post visible at{" "}
              <code className="font-mono">/bulletin/{slug}</code> once
              Story 2.9 ships. The slug becomes permanent (URL stability +
              SEO). Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirmPublish}>
              Publish
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
